import { cellToBoundary, latLngToCell, polygonToCells } from 'h3-js';

export type EditorialSourceType = 'geojson' | 'kml' | 'gpx' | 'existing';

export type EditorialGeometryType = 'POINT' | 'LINE' | 'POLYGON';

export type EditorialFeature = {
  geometry: any;
  properties: Record<string, any>;
};

export type EditorialImportResult = {
  source: EditorialSourceType;
  features: EditorialFeature[];
  warnings: string[];
};

export type EditorialBounds = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

export type EditorialStyleMode = 'auto' | 'single' | 'categorized' | 'choropleth';

export type EditorialStyleConfig = {
  point: {
    mode: 'auto' | 'single' | 'categorized' | 'choropleth';
    field?: string;
    color?: string;
    size?: number;
    sizeField?: string;
    sizeMin?: number;
    sizeMax?: number;
    opacity?: number;
    strokeWidth?: number;
    palette?: string;
    paletteReverse?: boolean;
    shape?: 'circle' | 'square' | 'diamond' | 'triangle' | 'pin' | 'hex' | 'star';
  };
  line: {
    mode: 'auto' | 'single' | 'categorized';
    field?: string;
    color?: string;
    width?: number;
    opacity?: number;
    palette?: string;
    dash?: 'solid' | 'dash' | 'dot' | 'dashdot';
  };
  polygon: {
    mode: 'auto' | 'single' | 'choropleth';
    field?: string;
    color?: string;
    fillOpacity?: number;
    strokeWidth?: number;
    opacity?: number;
    palette?: string;
    dash?: 'solid' | 'dash' | 'dot' | 'dashdot';
  };
};

export type EditorialDrawing = {
  geometry: string;
  type: EditorialGeometryType;
  style: Record<string, any>;
  properties?: Record<string, any>;
};

const DEFAULT_COLORS = {
  point: '#f59e0b',
  line: '#2563eb',
  polygon: '#10b981'
};

export const EDITORIAL_CATEGORY_PALETTES: Record<string, string[]> = {
  default: [
    '#2563eb',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#0ea5e9',
    '#22c55e',
    '#eab308',
    '#f97316',
    '#14b8a6',
    '#64748b',
    '#db2777',
    '#a855f7'
  ],
  pastel: [
    '#bae6fd',
    '#a7f3d0',
    '#fde68a',
    '#fecdd3',
    '#c7d2fe',
    '#bbf7d0',
    '#fed7aa',
    '#e9d5ff',
    '#fbcfe8',
    '#cffafe'
  ],
  bold: [
    '#1d4ed8',
    '#059669',
    '#d97706',
    '#dc2626',
    '#0ea5e9',
    '#16a34a',
    '#b45309',
    '#ea580c',
    '#0d9488',
    '#4f46e5'
  ],
  cool: [
    '#0ea5e9',
    '#2563eb',
    '#14b8a6',
    '#22c55e',
    '#06b6d4',
    '#38bdf8',
    '#3b82f6',
    '#10b981',
    '#0f766e',
    '#1e40af'
  ],
  sunset: [
    '#f97316',
    '#fb7185',
    '#f59e0b',
    '#ef4444',
    '#ec4899',
    '#f43f5e',
    '#fbbf24',
    '#fb923c',
    '#e11d48',
    '#c2410c'
  ],
  earth: [
    '#a16207',
    '#4d7c0f',
    '#b45309',
    '#6b7280',
    '#0f766e',
    '#7c3aed',
    '#ca8a04',
    '#14532d',
    '#78350f',
    '#4c1d95'
  ],
  mono: [
    '#111827',
    '#1f2937',
    '#374151',
    '#4b5563',
    '#6b7280',
    '#9ca3af',
    '#d1d5db',
    '#e5e7eb'
  ],
  vibrant: [
    '#0ea5e9',
    '#22c55e',
    '#f59e0b',
    '#ef4444',
    '#a855f7',
    '#14b8a6',
    '#e11d48',
    '#3b82f6',
    '#84cc16',
    '#f97316'
  ]
};

export const EDITORIAL_CHORO_PALETTES: Record<string, string[]> = {
  // Single-hue sequential
  amber: ['#fef3c7', '#fde68a', '#fbbf24', '#f59e0b', '#d97706'],
  teal: ['#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#0f766e'],
  blue: ['#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#1d4ed8'],
  red: ['#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#b91c1c'],
  gray: ['#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#4b5563'],
  green: ['#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#166534'],
  purple: ['#ede9fe', '#ddd6fe', '#c4b5fd', '#a78bfa', '#6d28d9'],
  slate: ['#f1f5f9', '#e2e8f0', '#cbd5e1', '#94a3b8', '#475569'],
  brown: ['#fef3c7', '#fde68a', '#fcd34d', '#d97706', '#92400e'],
  mint: ['#ecfdf3', '#d1fae5', '#a7f3d0', '#6ee7b7', '#047857'],
  // Multi-hue sequential & diverging
  heat: ['#fef9c3', '#fde047', '#f97316', '#dc2626', '#7f1d1d'],
  viridis: ['#fde725', '#5ec962', '#21918c', '#3b528b', '#440154'],
  plasma: ['#f0f921', '#f89540', '#cc4778', '#7e03a8', '#0d0887'],
  danger: ['#22c55e', '#84cc16', '#facc15', '#f97316', '#dc2626'],
  coolwarm: ['#3b82f6', '#93c5fd', '#f5f5f5', '#fca5a5', '#dc2626'],
  spectral: ['#d53e4f', '#fc8d59', '#fee08b', '#99d594', '#3288bd']
};

export const EDITORIAL_CATEGORY_PALETTE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'pastel', label: 'Pastel' },
  { value: 'bold', label: 'Bold' },
  { value: 'cool', label: 'Cool' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'earth', label: 'Earth' },
  { value: 'mono', label: 'Monochrome' },
  { value: 'vibrant', label: 'Vibrant' }
];

export const EDITORIAL_CHORO_PALETTE_OPTIONS = [
  // Multi-hue (recommended for data visualization)
  { value: 'heat', label: 'Heat (yellow→red)' },
  { value: 'danger', label: 'Danger (green→red)' },
  { value: 'viridis', label: 'Viridis' },
  { value: 'plasma', label: 'Plasma' },
  { value: 'coolwarm', label: 'Cool-Warm' },
  { value: 'spectral', label: 'Spectral' },
  // Single-hue sequential
  { value: 'red', label: 'Red' },
  { value: 'amber', label: 'Amber' },
  { value: 'teal', label: 'Teal' },
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'gray', label: 'Gray' },
  { value: 'slate', label: 'Slate' },
  { value: 'brown', label: 'Brown' },
  { value: 'mint', label: 'Mint' }
];

export const EDITORIAL_POINT_SHAPE_OPTIONS = [
  { value: 'circle', label: 'Circle' },
  { value: 'square', label: 'Square' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'triangle', label: 'Triangle' },
  { value: 'pin', label: 'Pin' },
  { value: 'hex', label: 'Hexagon' },
  { value: 'star', label: 'Star' }
];

export const EDITORIAL_LINE_STYLE_OPTIONS = [
  { value: 'solid', label: 'Solid' },
  { value: 'dash', label: 'Dashed' },
  { value: 'dot', label: 'Dotted' },
  { value: 'dashdot', label: 'Dash-dot' }
];

const MAX_FEATURES = 500;
const MAX_EDITORIAL_HEX_CELLS = 72;
const MIN_EDITORIAL_HEX_RES = 4;
const MAX_EDITORIAL_HEX_RES = 8;

const isWgs84Crs = (crsName?: string | null) => {
  if (!crsName) return true;
  const norm = crsName.toLowerCase();
  return (
    norm.includes('epsg:4326') ||
    norm.includes('epsg::4326') ||
    norm.includes('crs84') ||
    norm.includes('wgs 84')
  );
};

const extractCrsName = (geojson: any): string | null => {
  if (!geojson || !geojson.crs) return null;
  const crs = geojson.crs;
  if (typeof crs === 'string') return crs;
  if (typeof crs?.properties?.name === 'string') return crs.properties.name;
  if (typeof crs?.name === 'string') return crs.name;
  if (typeof crs?.properties?.href === 'string') return crs.properties.href;
  return null;
};

const parseCoordinateList = (text: string): Array<[number, number]> => {
  return text
    .trim()
    .split(/\s+/)
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const [lngRaw, latRaw] = pair.split(',');
      const lng = Number.parseFloat(lngRaw);
      const lat = Number.parseFloat(latRaw);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return [lng, lat] as [number, number];
    })
    .filter(Boolean) as Array<[number, number]>;
};

const closeRing = (coords: Array<[number, number]>): Array<[number, number]> => {
  if (coords.length < 2) return coords;
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return coords;
  return [...coords, first];
};

const normalizeGeometry = (geometry: any, properties: Record<string, any>, out: EditorialFeature[]) => {
  if (!geometry || typeof geometry !== 'object' || !geometry.type) return;
  switch (geometry.type) {
    case 'Point':
    case 'LineString':
    case 'Polygon':
      out.push({ geometry, properties });
      return;
    case 'MultiPoint':
      (geometry.coordinates || []).forEach((coord: any) => {
        out.push({ geometry: { type: 'Point', coordinates: coord }, properties });
      });
      return;
    case 'MultiLineString':
      (geometry.coordinates || []).forEach((coords: any) => {
        out.push({ geometry: { type: 'LineString', coordinates: coords }, properties });
      });
      return;
    case 'MultiPolygon':
      (geometry.coordinates || []).forEach((coords: any) => {
        out.push({ geometry: { type: 'Polygon', coordinates: coords }, properties });
      });
      return;
    case 'GeometryCollection':
      (geometry.geometries || []).forEach((geom: any) => normalizeGeometry(geom, properties, out));
      return;
    default:
      return;
  }
};

const normalizeGeoJson = (geojson: any): EditorialFeature[] => {
  const out: EditorialFeature[] = [];
  if (!geojson) return out;
  if (geojson.type === 'FeatureCollection') {
    (geojson.features || []).forEach((feature: any) => {
      normalizeGeometry(feature?.geometry, feature?.properties || {}, out);
    });
    return out;
  }
  if (geojson.type === 'Feature') {
    normalizeGeometry(geojson.geometry, geojson.properties || {}, out);
    return out;
  }
  if (geojson.type && geojson.coordinates) {
    normalizeGeometry(geojson, {}, out);
  }
  return out;
};

export const buildEditorialFeaturesFromDrawings = (
  drawings: Array<{ geometry: string | any; properties?: Record<string, any> | null | undefined }>
): EditorialFeature[] => {
  const out: EditorialFeature[] = [];
  if (!Array.isArray(drawings) || drawings.length === 0) return out;

  drawings.forEach((drawing) => {
    if (!drawing || !drawing.geometry) return;
    let geometry: any = drawing.geometry;
    if (typeof geometry === 'string') {
      try {
        geometry = JSON.parse(geometry);
      } catch {
        return;
      }
    }
    if (!geometry || typeof geometry !== 'object') return;
    const properties = (drawing.properties && typeof drawing.properties === 'object') ? drawing.properties : {};
    if (geometry.type === 'Feature' && geometry.geometry) {
      normalizeGeometry(geometry.geometry, geometry.properties || properties, out);
      return;
    }
    normalizeGeometry(geometry, properties, out);
  });

  return out;
};

export const parseGeoJsonText = (text: string): EditorialImportResult => {
  const warnings: string[] = [];
  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch (err: any) {
    throw new Error('GeoJSON file could not be parsed.');
  }
  const crsName = extractCrsName(parsed);
  if (crsName && !isWgs84Crs(crsName)) {
    throw new Error(`Unsupported CRS (${crsName}). Use EPSG:4326 / WGS84.`);
  }
  const features = normalizeGeoJson(parsed);
  if (!features.length) {
    warnings.push('No supported geometries found in GeoJSON.');
  }
  return { source: 'geojson', features, warnings };
};

export const parseKmlText = (text: string): EditorialImportResult => {
  const warnings: string[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new Error('KML file could not be parsed.');
  }
  const features: EditorialFeature[] = [];
  const placemarks = Array.from(doc.getElementsByTagName('Placemark'));
  placemarks.forEach((placemark) => {
    const props: Record<string, any> = {};
    const nameNode = placemark.getElementsByTagName('name')[0];
    const descNode = placemark.getElementsByTagName('description')[0];
    if (nameNode?.textContent) props.name = nameNode.textContent.trim();
    if (descNode?.textContent) props.description = descNode.textContent.trim();
    Array.from(placemark.getElementsByTagName('Data')).forEach((dataNode) => {
      const key = dataNode.getAttribute('name') || '';
      const valueNode = dataNode.getElementsByTagName('value')[0];
      if (key && valueNode?.textContent) props[key] = valueNode.textContent.trim();
    });
    Array.from(placemark.getElementsByTagName('SimpleData')).forEach((dataNode) => {
      const key = dataNode.getAttribute('name') || '';
      if (key && dataNode.textContent) props[key] = dataNode.textContent.trim();
    });

    const pointNode = placemark.getElementsByTagName('Point')[0];
    if (pointNode) {
      const coordsNode = pointNode.getElementsByTagName('coordinates')[0];
      if (coordsNode?.textContent) {
        const coords = parseCoordinateList(coordsNode.textContent);
        if (coords.length) {
          features.push({ geometry: { type: 'Point', coordinates: coords[0] }, properties: props });
        }
      }
    }

    const lineNode = placemark.getElementsByTagName('LineString')[0];
    if (lineNode) {
      const coordsNode = lineNode.getElementsByTagName('coordinates')[0];
      if (coordsNode?.textContent) {
        const coords = parseCoordinateList(coordsNode.textContent);
        if (coords.length >= 2) {
          features.push({ geometry: { type: 'LineString', coordinates: coords }, properties: props });
        }
      }
    }

    const polygonNode = placemark.getElementsByTagName('Polygon')[0];
    if (polygonNode) {
      const coordsNode = polygonNode.getElementsByTagName('outerBoundaryIs')[0]?.getElementsByTagName('coordinates')[0];
      if (coordsNode?.textContent) {
        const coords = closeRing(parseCoordinateList(coordsNode.textContent));
        if (coords.length >= 4) {
          features.push({ geometry: { type: 'Polygon', coordinates: [coords] }, properties: props });
        }
      }
    }
  });
  if (!features.length) {
    warnings.push('No supported geometries found in KML.');
  }
  return { source: 'kml', features, warnings };
};

export const parseGpxText = (text: string): EditorialImportResult => {
  const warnings: string[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new Error('GPX file could not be parsed.');
  }
  const features: EditorialFeature[] = [];

  Array.from(doc.getElementsByTagName('wpt')).forEach((node) => {
    const lat = Number.parseFloat(node.getAttribute('lat') || '');
    const lng = Number.parseFloat(node.getAttribute('lon') || '');
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const props: Record<string, any> = {};
    const nameNode = node.getElementsByTagName('name')[0];
    if (nameNode?.textContent) props.name = nameNode.textContent.trim();
    features.push({ geometry: { type: 'Point', coordinates: [lng, lat] }, properties: props });
  });

  Array.from(doc.getElementsByTagName('trk')).forEach((trk) => {
    const props: Record<string, any> = {};
    const nameNode = trk.getElementsByTagName('name')[0];
    if (nameNode?.textContent) props.name = nameNode.textContent.trim();
    Array.from(trk.getElementsByTagName('trkseg')).forEach((seg) => {
      const coords: Array<[number, number]> = [];
      Array.from(seg.getElementsByTagName('trkpt')).forEach((pt) => {
        const lat = Number.parseFloat(pt.getAttribute('lat') || '');
        const lng = Number.parseFloat(pt.getAttribute('lon') || '');
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          coords.push([lng, lat]);
        }
      });
      if (coords.length >= 2) {
        features.push({ geometry: { type: 'LineString', coordinates: coords }, properties: props });
      }
    });
  });

  Array.from(doc.getElementsByTagName('rte')).forEach((route) => {
    const props: Record<string, any> = {};
    const nameNode = route.getElementsByTagName('name')[0];
    if (nameNode?.textContent) props.name = nameNode.textContent.trim();
    const coords: Array<[number, number]> = [];
    Array.from(route.getElementsByTagName('rtept')).forEach((pt) => {
      const lat = Number.parseFloat(pt.getAttribute('lat') || '');
      const lng = Number.parseFloat(pt.getAttribute('lon') || '');
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        coords.push([lng, lat]);
      }
    });
    if (coords.length >= 2) {
      features.push({ geometry: { type: 'LineString', coordinates: coords }, properties: props });
    }
  });

  if (!features.length) {
    warnings.push('No supported geometries found in GPX.');
  }
  return { source: 'gpx', features, warnings };
};

export const parseEditorialText = (text: string, fileName: string): EditorialImportResult => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.kml')) {
    return parseKmlText(text);
  }
  if (lower.endsWith('.gpx')) {
    return parseGpxText(text);
  }
  return parseGeoJsonText(text);
};

export const summarizeEditorialProperties = (features: EditorialFeature[]) => {
  const numeric = new Set<string>();
  const categorical = new Set<string>();
  features.forEach((feature) => {
    const props = feature.properties || {};
    Object.entries(props).forEach(([key, value]) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        numeric.add(key);
        return;
      }
      if (typeof value === 'string' && value.trim().length > 0) {
        const trimmed = value.trim();
        const parsed = Number.parseFloat(trimmed);
        if (Number.isFinite(parsed)) {
          numeric.add(key);
        } else {
          categorical.add(key);
        }
        return;
      }
      if (typeof value === 'boolean') {
        categorical.add(key);
      }
    });
  });
  return {
    numericKeys: Array.from(numeric),
    categoricalKeys: Array.from(categorical)
  };
};

const accumulateCoordinates = (coords: any, acc: { sumLat: number; sumLng: number; count: number }) => {
  if (!Array.isArray(coords)) return;
  if (
    coords.length >= 2 &&
    typeof coords[0] === 'number' &&
    typeof coords[1] === 'number' &&
    Number.isFinite(coords[0]) &&
    Number.isFinite(coords[1])
  ) {
    acc.sumLng += coords[0];
    acc.sumLat += coords[1];
    acc.count += 1;
    return;
  }
  coords.forEach((entry) => accumulateCoordinates(entry, acc));
};

export const getEditorialRepresentativeLocation = (features: EditorialFeature[]) => {
  const acc = { sumLat: 0, sumLng: 0, count: 0 };
  features.forEach((feature) => {
    accumulateCoordinates(feature?.geometry?.coordinates, acc);
  });
  if (acc.count === 0) return null;
  return { lat: acc.sumLat / acc.count, lng: acc.sumLng / acc.count };
};

export const getEditorialBounds = (features: EditorialFeature[]): EditorialBounds | null => {
  const bounds = { minLat: Number.POSITIVE_INFINITY, maxLat: Number.NEGATIVE_INFINITY, minLng: Number.POSITIVE_INFINITY, maxLng: Number.NEGATIVE_INFINITY };
  const pushCoord = (lng: number, lat: number) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    bounds.minLat = Math.min(bounds.minLat, lat);
    bounds.maxLat = Math.max(bounds.maxLat, lat);
    bounds.minLng = Math.min(bounds.minLng, lng);
    bounds.maxLng = Math.max(bounds.maxLng, lng);
  };
  const walk = (coords: any) => {
    if (!Array.isArray(coords)) return;
    if (
      coords.length >= 2 &&
      typeof coords[0] === 'number' &&
      typeof coords[1] === 'number' &&
      Number.isFinite(coords[0]) &&
      Number.isFinite(coords[1])
    ) {
      pushCoord(coords[0], coords[1]);
      return;
    }
    coords.forEach((entry) => walk(entry));
  };
  features.forEach((feature) => walk(feature?.geometry?.coordinates));
  if (!Number.isFinite(bounds.minLat) || !Number.isFinite(bounds.minLng)) return null;
  return bounds;
};

const approxLngKm = (deltaLng: number, atLat: number) => {
  return Math.abs(deltaLng) * 111.32 * Math.cos((atLat * Math.PI) / 180);
};

const approxLatKm = (deltaLat: number) => {
  return Math.abs(deltaLat) * 110.57;
};

const chooseEditorialHexResolution = (bounds: EditorialBounds | null) => {
  if (!bounds) return 6;
  const centerLat = (bounds.minLat + bounds.maxLat) / 2;
  const widthKm = approxLngKm(bounds.maxLng - bounds.minLng, centerLat);
  const heightKm = approxLatKm(bounds.maxLat - bounds.minLat);
  const spanKm = Math.max(widthKm, heightKm);
  if (spanKm <= 4) return MAX_EDITORIAL_HEX_RES;
  if (spanKm <= 12) return 7;
  if (spanKm <= 35) return 6;
  if (spanKm <= 90) return 5;
  return MIN_EDITORIAL_HEX_RES;
};

const makeHexPolygonGeometry = (cell: string) => {
  const boundary = cellToBoundary(cell, false)
    .map(([lat, lng]) => [lng, lat]);
  const closed = closeRing(boundary as Array<[number, number]>);
  return {
    type: 'Polygon',
    coordinates: [closed]
  };
};

const fallbackHexGeometry = (geometry: any, bounds: EditorialBounds | null) => {
  const centerLat = bounds ? (bounds.minLat + bounds.maxLat) / 2 : Number(geometry?.coordinates?.[0]?.[0]?.[1]);
  const centerLng = bounds ? (bounds.minLng + bounds.maxLng) / 2 : Number(geometry?.coordinates?.[0]?.[0]?.[0]);
  if (!Number.isFinite(centerLat) || !Number.isFinite(centerLng)) return [];
  try {
    const cell = latLngToCell(centerLat, centerLng, chooseEditorialHexResolution(bounds));
    return [makeHexPolygonGeometry(cell)];
  } catch {
    return geometry ? [geometry] : [];
  }
};

const isLikelyHexCellPolygon = (geometry: any) => {
  const ring = geometry?.coordinates?.[0];
  return geometry?.type === 'Polygon' && Array.isArray(ring) && ring.length === 7;
};

export const buildEditorialHexCellGeometries = (geometry: any): any[] => {
  if (!geometry || geometry.type !== 'Polygon' || !Array.isArray(geometry.coordinates)) {
    return geometry ? [geometry] : [];
  }
  if (isLikelyHexCellPolygon(geometry)) {
    return [geometry];
  }

  const bounds = getEditorialBounds([{ geometry, properties: {} }]);
  const startRes = chooseEditorialHexResolution(bounds);

  for (let res = startRes; res >= MIN_EDITORIAL_HEX_RES; res -= 1) {
    try {
      const cells = polygonToCells(geometry.coordinates, res, true);
      if (!cells.length) continue;
      if (cells.length > MAX_EDITORIAL_HEX_CELLS && res > MIN_EDITORIAL_HEX_RES) {
        continue;
      }
      return cells.slice(0, MAX_EDITORIAL_HEX_CELLS).map(makeHexPolygonGeometry);
    } catch {
      continue;
    }
  }

  return fallbackHexGeometry(geometry, bounds);
};

const parseNumber = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const num = Number.parseFloat(trimmed);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

const normalizeHexColor = (value: string): string => {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return '';
  if (!trimmed.startsWith('#')) return trimmed;
  if (trimmed.length === 4) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return trimmed;
};

const colorToLuminance = (value: string): number | null => {
  const hex = normalizeHexColor(value);
  if (!hex.startsWith('#') || hex.length !== 7) return null;
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  if (![r, g, b].every((n) => Number.isFinite(n))) return null;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const pearsonCorrelation = (xs: number[], ys: number[]): number => {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return 0;
  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < n; i += 1) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;
  let num = 0;
  let denX = 0;
  let denY = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const denom = Math.sqrt(denX * denY);
  if (!Number.isFinite(denom) || denom === 0) return 0;
  return num / denom;
};

const medianValue = (values: number[]): number | null => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

const computePaletteMatch = (palette: string[], colors: Set<string>) => {
  const normalized = palette.map(normalizeHexColor);
  const paletteSet = new Set(normalized);
  let matches = 0;
  colors.forEach((c) => {
    if (paletteSet.has(c)) matches += 1;
  });
  const denom = Math.min(colors.size, normalized.length);
  const score = denom > 0 ? matches / denom : 0;
  return { score, colors: normalized, matches };
};

type DrawingStyleLike = {
  type: EditorialGeometryType;
  style?: Record<string, any> | null;
  properties?: Record<string, any> | null | undefined;
};

export const inferEditorialStyleFromDrawings = (
  drawings: DrawingStyleLike[]
): EditorialStyleConfig => {
  const fallback: EditorialStyleConfig = {
    point: { mode: 'auto', shape: 'pin', sizeMin: 4, sizeMax: 12 },
    line: { mode: 'auto' },
    polygon: { mode: 'auto' }
  };
  const style: EditorialStyleConfig = {
    point: { ...fallback.point },
    line: { ...fallback.line },
    polygon: { ...fallback.polygon }
  };

  if (!Array.isArray(drawings) || drawings.length === 0) return style;

  const dashFromArray = (dash?: string) => {
    if (!dash) return undefined;
    const trimmed = dash.trim();
    if (trimmed === '8 6') return 'dash';
    if (trimmed === '2 6') return 'dot';
    if (trimmed === '8 4 2 4') return 'dashdot';
    return undefined;
  };

  const pointDrawings = drawings.filter((d) => d.type === 'POINT');
  const lineDrawings = drawings.filter((d) => d.type === 'LINE');
  const polygonDrawings = drawings.filter((d) => d.type === 'POLYGON');

  if (lineDrawings.length) {
    const first = lineDrawings[0]?.style || {};
    style.line = {
      mode: 'single',
      color: first.color || DEFAULT_COLORS.line,
      dash: (dashFromArray(first.dashArray) || 'solid') as EditorialStyleConfig['line']['dash'],
      opacity: first.opacity ?? 0.9,
      width: first.weight ?? 2,
      palette: fallback.line.palette
    };
  }

  if (polygonDrawings.length) {
    const first = polygonDrawings[0]?.style || {};
    style.polygon = {
      mode: 'single',
      color: first.color || DEFAULT_COLORS.polygon,
      dash: (dashFromArray(first.dashArray) || 'solid') as EditorialStyleConfig['polygon']['dash'],
      fillOpacity: first.fillOpacity ?? 0.35,
      strokeWidth: first.weight ?? 2,
      opacity: first.opacity ?? 0.9,
      palette: fallback.polygon.palette
    };
  }

  if (!pointDrawings.length) return style;

  const firstPointStyle = pointDrawings[0]?.style || {};
  const basePointColor = firstPointStyle.fillColor || firstPointStyle.color || DEFAULT_COLORS.point;
  const baseRadius = parseNumber(firstPointStyle.radius) ?? 6;
  style.point = {
    ...style.point,
    mode: 'single',
    color: basePointColor,
    shape: (firstPointStyle.shape || style.point.shape || 'pin') as EditorialStyleConfig['point']['shape'],
    opacity: firstPointStyle.fillOpacity ?? firstPointStyle.opacity ?? 0.7,
    strokeWidth: firstPointStyle.weight ?? 2,
    size: baseRadius,
    palette: style.point.palette
  };

  const pointRecords = pointDrawings.map((drawing) => {
    const s = drawing.style || {};
    const colorRaw = typeof s.fillColor === 'string' ? s.fillColor : (typeof s.color === 'string' ? s.color : '');
    const color = colorRaw ? normalizeHexColor(colorRaw) : '';
    const radius = parseNumber(s.radius);
    return {
      props: (drawing.properties && typeof drawing.properties === 'object') ? drawing.properties : {},
      color,
      radius
    };
  });

  const colors = pointRecords.map((r) => r.color).filter(Boolean);
  const uniqueColors = new Set(colors);
  const radiusValues = pointRecords.map((r) => r.radius).filter((v): v is number => Number.isFinite(v));
  const uniqueRadii = new Set(radiusValues.map((v) => Math.round(v * 1000) / 1000));

  const pointFeatures = buildEditorialFeaturesFromDrawings(pointDrawings as any);
  const { numericKeys, categoricalKeys } = summarizeEditorialProperties(pointFeatures);

  let sizeField: string | null = null;
  let sizeCorr = 0;
  if (uniqueRadii.size > 1 && numericKeys.length) {
    numericKeys.forEach((key) => {
      const xs: number[] = [];
      const ys: number[] = [];
      pointRecords.forEach((record) => {
        const value = parseNumber(record.props?.[key]);
        if (value === null || !Number.isFinite(record.radius)) return;
        xs.push(value);
        ys.push(record.radius as number);
      });
      if (xs.length < 3) return;
      const corr = Math.abs(pearsonCorrelation(xs, ys));
      if (corr > sizeCorr) {
        sizeCorr = corr;
        sizeField = key;
      }
    });
  }

  if (sizeField && sizeCorr >= 0.45) {
    style.point.sizeField = sizeField;
    const minRadius = Math.min(...radiusValues);
    const maxRadius = Math.max(...radiusValues);
    if (Number.isFinite(minRadius) && Number.isFinite(maxRadius) && minRadius !== maxRadius) {
      style.point.sizeMin = minRadius;
      style.point.sizeMax = maxRadius;
    }
  } else if (uniqueRadii.size > 0) {
    const median = medianValue(radiusValues);
    if (median !== null) {
      style.point.size = median;
    }
  }

  if (uniqueColors.size <= 1) {
    style.point.mode = 'single';
    return style;
  }

  const choroMatches = Object.entries(EDITORIAL_CHORO_PALETTES).map(([name, palette]) => ({
    name,
    palette,
    ...computePaletteMatch(palette, uniqueColors)
  }));
  const catMatches = Object.entries(EDITORIAL_CATEGORY_PALETTES).map(([name, palette]) => ({
    name,
    palette,
    ...computePaletteMatch(palette, uniqueColors)
  }));
  const bestChoro = choroMatches.sort((a, b) => b.score - a.score)[0];
  const bestCat = catMatches.sort((a, b) => b.score - a.score)[0];

  const hasNumeric = numericKeys.length > 0;
  const hasCategorical = categoricalKeys.length > 0;

  let pointMode: EditorialStyleConfig['point']['mode'] = 'auto';
  if (bestChoro && bestChoro.score >= 0.5 && (bestChoro.score >= (bestCat?.score || 0) || !hasCategorical)) {
    pointMode = hasNumeric ? 'choropleth' : (hasCategorical ? 'categorized' : 'auto');
  } else if (bestCat && bestCat.score >= 0.5) {
    pointMode = 'categorized';
  } else if (hasNumeric) {
    pointMode = 'choropleth';
  } else if (hasCategorical) {
    pointMode = 'categorized';
  } else {
    pointMode = 'single';
  }

  style.point.mode = pointMode;
  if (pointMode === 'choropleth') {
    style.point.palette = bestChoro?.name || style.point.palette || 'heat';
  }
  if (pointMode === 'categorized') {
    style.point.palette = bestCat?.name || style.point.palette || 'default';
  }

  const buildColorIndexMap = (paletteColors?: string[]) => {
    const map = new Map<string, number>();
    const normalizedPalette = paletteColors ? paletteColors.map(normalizeHexColor) : [];
    normalizedPalette.forEach((color, idx) => {
      map.set(color, idx);
    });
    const missing: Array<{ color: string; lum: number }> = [];
    uniqueColors.forEach((color) => {
      if (!map.has(color)) {
        const lum = colorToLuminance(color);
        if (lum !== null) {
          missing.push({ color, lum });
        }
      }
    });
    if (missing.length) {
      missing.sort((a, b) => a.lum - b.lum);
      const start = map.size;
      missing.forEach((entry, idx) => {
        map.set(entry.color, start + idx);
      });
    }
    return map;
  };

  if (pointMode === 'choropleth' && numericKeys.length) {
    const paletteColors = bestChoro?.palette;
    const colorIndexMap = buildColorIndexMap(paletteColors);
    let bestField: string | null = (sizeField && numericKeys.includes(sizeField)) ? sizeField : null;
    let bestCorr = 0;
    let bestSignedCorr = 0;
    numericKeys.forEach((key) => {
      const xs: number[] = [];
      const ys: number[] = [];
      pointRecords.forEach((record) => {
        const value = parseNumber(record.props?.[key]);
        const colorIndex = record.color ? colorIndexMap.get(record.color) : undefined;
        if (value === null || colorIndex === undefined) return;
        xs.push(value);
        ys.push(colorIndex);
      });
      if (xs.length < 3) return;
      const corr = pearsonCorrelation(xs, ys);
      const score = Math.abs(corr);
      if (score > bestCorr) {
        bestCorr = score;
        bestField = key;
        bestSignedCorr = corr;
      }
    });
    if (!bestField && numericKeys.length) {
      bestField = numericKeys[0];
    }
    if (bestField) {
      style.point.field = bestField;
      if (Math.abs(bestSignedCorr) >= 0.25) {
        style.point.paletteReverse = bestSignedCorr < 0;
      }
      if (!style.point.sizeField && uniqueRadii.size > 1) {
        style.point.sizeField = bestField;
        const minRadius = Math.min(...radiusValues);
        const maxRadius = Math.max(...radiusValues);
        if (Number.isFinite(minRadius) && Number.isFinite(maxRadius) && minRadius !== maxRadius) {
          style.point.sizeMin = minRadius;
          style.point.sizeMax = maxRadius;
        }
      }
    }
  }

  if (pointMode === 'categorized') {
    const candidates = categoricalKeys.length ? categoricalKeys : numericKeys;
    let bestField = candidates[0] || '';
    let bestConsistency = 0;
    candidates.forEach((key) => {
      const counts = new Map<string, Map<string, number>>();
      pointRecords.forEach((record) => {
        const raw = record.props?.[key];
        if (raw === null || raw === undefined) return;
        const value = String(raw);
        if (!value) return;
        if (!record.color) return;
        if (!counts.has(value)) counts.set(value, new Map<string, number>());
        const bucket = counts.get(value)!;
        bucket.set(record.color, (bucket.get(record.color) || 0) + 1);
      });
      if (!counts.size) return;
      let totalRatio = 0;
      counts.forEach((bucket) => {
        let maxCount = 0;
        let sum = 0;
        bucket.forEach((count) => {
          sum += count;
          if (count > maxCount) maxCount = count;
        });
        totalRatio += sum ? maxCount / sum : 0;
      });
      const avg = totalRatio / counts.size;
      if (avg > bestConsistency) {
        bestConsistency = avg;
        bestField = key;
      }
    });
    if (bestField) {
      style.point.field = bestField;
      if (!style.point.sizeField && uniqueRadii.size > 1 && numericKeys.includes(bestField)) {
        style.point.sizeField = bestField;
        const minRadius = Math.min(...radiusValues);
        const maxRadius = Math.max(...radiusValues);
        if (Number.isFinite(minRadius) && Number.isFinite(maxRadius) && minRadius !== maxRadius) {
          style.point.sizeMin = minRadius;
          style.point.sizeMax = maxRadius;
        }
      }
    }
  }

  return style;
};

const pickPropertyColor = (properties: Record<string, any>, fallback: string) => {
  if (!properties) return fallback;
  const candidateKeys = [
    'stroke',
    'strokeColor',
    'stroke-color',
    'fill',
    'fillColor',
    'fill-color',
    'color',
    'marker-color',
    'markerColor'
  ];
  for (const key of candidateKeys) {
    const value = properties[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return fallback;
};

const pickPropertyNumber = (properties: Record<string, any>, keys: string[], fallback: number) => {
  if (!properties) return fallback;
  for (const key of keys) {
    const value = parseNumber(properties[key]);
    if (value !== null) return value;
  }
  return fallback;
};

const buildCategoryColorMap = (values: string[], palette: string[]) => {
  const map = new Map<string, string>();
  let index = 0;
  values.forEach((value) => {
    if (map.has(value)) return;
    map.set(value, palette[index % palette.length]);
    index += 1;
  });
  return map;
};

const buildChoroplethStops = (values: number[], palette: string[]) => {
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return null;
  const step = (max - min) / palette.length;
  return { min, max, step, palette };
};

const colorForChoropleth = (value: number | null, stops: { min: number; max: number; step: number; palette: string[] } | null) => {
  if (value === null || !stops) return EDITORIAL_CHORO_PALETTES.amber[0];
  const palette = stops.palette || EDITORIAL_CHORO_PALETTES.amber;
  const idx = Math.min(palette.length - 1, Math.max(0, Math.floor((value - stops.min) / stops.step)));
  return palette[idx];
};

const geometryToType = (geometry: any): EditorialGeometryType | null => {
  if (!geometry || typeof geometry !== 'object') return null;
  if (geometry.type === 'Point') return 'POINT';
  if (geometry.type === 'LineString') return 'LINE';
  if (geometry.type === 'Polygon') return 'POLYGON';
  return null;
};

const expandEditorialFeature = (feature: EditorialFeature): EditorialFeature[] => {
  const geometry = feature.geometry;
  if (!geometry || typeof geometry !== 'object') return [];
  if (geometry.type === 'Feature' && geometry.geometry) {
    return expandEditorialFeature({ geometry: geometry.geometry, properties: feature.properties });
  }
  if (geometry.type === 'GeometryCollection' && Array.isArray(geometry.geometries)) {
    return geometry.geometries.flatMap((geom: any) =>
      expandEditorialFeature({ geometry: geom, properties: feature.properties })
    );
  }
  if (geometry.type === 'MultiPoint' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates
      .filter((coord: any) => Array.isArray(coord) && coord.length >= 2)
      .map((coord: any) => ({
        geometry: { type: 'Point', coordinates: coord },
        properties: { ...(feature.properties || {}) }
      }));
  }
  if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates
      .filter((line: any) => Array.isArray(line))
      .map((line: any) => ({
        geometry: { type: 'LineString', coordinates: line },
        properties: { ...(feature.properties || {}) }
      }));
  }
  if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates
      .filter((poly: any) => Array.isArray(poly))
      .map((poly: any) => ({
        geometry: { type: 'Polygon', coordinates: poly },
        properties: { ...(feature.properties || {}) }
      }));
  }
  return [feature];
};

export const buildEditorialDrawings = (features: EditorialFeature[], style: EditorialStyleConfig): EditorialDrawing[] => {
  const drawings: EditorialDrawing[] = [];
  const expandedFeatures = features.flatMap((feature) => expandEditorialFeature(feature));
  const polygonFeatures = expandedFeatures.filter((f) => geometryToType(f.geometry) === 'POLYGON');
  const lineFeatures = expandedFeatures.filter((f) => geometryToType(f.geometry) === 'LINE');
  const pointFeatures = expandedFeatures.filter((f) => geometryToType(f.geometry) === 'POINT');
  const pointPalette = EDITORIAL_CATEGORY_PALETTES[style.point.palette || 'default'] || EDITORIAL_CATEGORY_PALETTES.default;
  const linePalette = EDITORIAL_CATEGORY_PALETTES[style.line.palette || 'default'] || EDITORIAL_CATEGORY_PALETTES.default;
  const polygonPalette = EDITORIAL_CHORO_PALETTES[style.polygon.palette || 'amber'] || EDITORIAL_CHORO_PALETTES.amber;

  const polygonValues = style.polygon.field
    ? polygonFeatures
      .map((f) => parseNumber(f.properties?.[style.polygon.field as string]))
      .filter((v): v is number => v !== null)
    : [];
  const polygonStops = style.polygon.mode === 'choropleth' ? buildChoroplethStops(polygonValues, polygonPalette) : null;

  const lineCategories = style.line.field
    ? Array.from(new Set(lineFeatures.map((f) => String(f.properties?.[style.line.field as string] ?? '')))).filter(Boolean)
    : [];
  const lineCategoryMap = style.line.mode === 'categorized' ? buildCategoryColorMap(lineCategories, linePalette) : null;

  const pointCategories = style.point.field
    ? Array.from(new Set(pointFeatures.map((f) => String(f.properties?.[style.point.field as string] ?? '')))).filter(Boolean)
    : [];
  const pointCategoryMap = style.point.mode === 'categorized' ? buildCategoryColorMap(pointCategories, pointPalette) : null;

  // Choropleth support for points
  const pointChoroplethPaletteBase = EDITORIAL_CHORO_PALETTES[style.point.palette || 'heat'] || EDITORIAL_CHORO_PALETTES.heat;
  const pointChoroplethPalette = style.point.paletteReverse ? [...pointChoroplethPaletteBase].reverse() : pointChoroplethPaletteBase;
  const pointChoroplethValues = style.point.field
    ? pointFeatures
      .map((f) => parseNumber(f.properties?.[style.point.field as string]))
      .filter((v): v is number => v !== null)
    : [];
  const pointChoroplethStops = style.point.mode === 'choropleth' ? buildChoroplethStops(pointChoroplethValues, pointChoroplethPalette) : null;
  const pointSizeValues = style.point.sizeField
    ? pointFeatures
      .map((f) => parseNumber(f.properties?.[style.point.sizeField as string]))
      .filter((v): v is number => v !== null)
    : [];
  const pointSizeRange = style.point.sizeField && pointSizeValues.length
    ? { min: Math.min(...pointSizeValues), max: Math.max(...pointSizeValues) }
    : null;

  const resolvePolygonColor = (feature: EditorialFeature) => {
    if (style.polygon.mode === 'single') return style.polygon.color || DEFAULT_COLORS.polygon;
    if (style.polygon.mode === 'choropleth') {
      const value = style.polygon.field ? parseNumber(feature.properties?.[style.polygon.field]) : null;
      return colorForChoropleth(value, polygonStops);
    }
    return pickPropertyColor(feature.properties, DEFAULT_COLORS.polygon);
  };

  const resolveLineColor = (feature: EditorialFeature) => {
    if (style.line.mode === 'single') return style.line.color || DEFAULT_COLORS.line;
    if (style.line.mode === 'categorized' && style.line.field && lineCategoryMap) {
      const key = String(feature.properties?.[style.line.field] ?? '');
      return lineCategoryMap.get(key) || DEFAULT_COLORS.line;
    }
    return pickPropertyColor(feature.properties, DEFAULT_COLORS.line);
  };

  const resolvePointColor = (feature: EditorialFeature) => {
    if (style.point.mode === 'single') return style.point.color || DEFAULT_COLORS.point;
    if (style.point.mode === 'categorized' && style.point.field && pointCategoryMap) {
      const key = String(feature.properties?.[style.point.field] ?? '');
      return pointCategoryMap.get(key) || DEFAULT_COLORS.point;
    }
    if (style.point.mode === 'choropleth' && style.point.field) {
      const value = parseNumber(feature.properties?.[style.point.field]);
      return colorForChoropleth(value, pointChoroplethStops);
    }
    return pickPropertyColor(feature.properties, DEFAULT_COLORS.point);
  };

  const resolvePointRadius = (feature: EditorialFeature) => {
    const baseRadius = style.point.size ?? pickPropertyNumber(feature.properties, ['radius', 'point-size', 'pointSize'], 6);
    if (!style.point.sizeField || !pointSizeRange) return baseRadius;
    const value = parseNumber(feature.properties?.[style.point.sizeField]);
    if (value === null) return baseRadius;
    let minSize = Number.isFinite(style.point.sizeMin) ? Number(style.point.sizeMin) : 4;
    let maxSize = Number.isFinite(style.point.sizeMax) ? Number(style.point.sizeMax) : Math.max(minSize + 2, 12);
    if (maxSize <= minSize) maxSize = minSize + 2;
    minSize = Math.max(2, minSize);
    maxSize = Math.max(minSize + 1, maxSize);
    if (pointSizeRange.min === pointSizeRange.max) return maxSize;
    const t = (value - pointSizeRange.min) / (pointSizeRange.max - pointSizeRange.min);
    const clamped = Math.min(Math.max(t, 0), 1);
    const scaled = minSize + clamped * (maxSize - minSize);
    return Math.min(Math.max(scaled, 2), 40);
  };

  const dashArrayFor = (dash?: string) => {
    if (!dash || dash === 'solid') return undefined;
    if (dash === 'dash') return '8 6';
    if (dash === 'dot') return '2 6';
    if (dash === 'dashdot') return '8 4 2 4';
    return undefined;
  };

  const pushDrawing = (feature: EditorialFeature, type: EditorialGeometryType) => {
    const color = type === 'POLYGON'
      ? resolvePolygonColor(feature)
      : type === 'LINE'
        ? resolveLineColor(feature)
        : resolvePointColor(feature);
    const weight = type === 'POINT'
      ? (style.point.strokeWidth ?? pickPropertyNumber(feature.properties, ['stroke-width', 'strokeWidth', 'weight', 'lineWidth'], 2))
      : type === 'LINE'
        ? (style.line.width ?? pickPropertyNumber(feature.properties, ['stroke-width', 'strokeWidth', 'weight', 'lineWidth'], 2))
        : (style.polygon.strokeWidth ?? pickPropertyNumber(feature.properties, ['stroke-width', 'strokeWidth', 'weight', 'lineWidth'], 2));
    const opacity = type === 'LINE'
      ? (style.line.opacity ?? pickPropertyNumber(feature.properties, ['stroke-opacity', 'strokeOpacity', 'opacity'], 0.9))
      : type === 'POLYGON'
        ? (style.polygon.opacity ?? pickPropertyNumber(feature.properties, ['stroke-opacity', 'strokeOpacity', 'opacity'], 0.9))
        : (style.point.opacity ?? pickPropertyNumber(feature.properties, ['stroke-opacity', 'strokeOpacity', 'opacity'], 0.9));
    const fillOpacity = type === 'POLYGON'
      ? (style.polygon.fillOpacity ?? pickPropertyNumber(feature.properties, ['fill-opacity', 'fillOpacity'], 0.35))
      : type === 'POINT'
        ? (style.point.opacity ?? pickPropertyNumber(feature.properties, ['fill-opacity', 'fillOpacity'], 0.7))
        : 0.7;
    const radius = type === 'POINT'
      ? resolvePointRadius(feature)
      : undefined;
    const dashArray = type === 'LINE'
      ? dashArrayFor(style.line.dash)
      : type === 'POLYGON'
        ? dashArrayFor(style.polygon.dash)
        : undefined;
    const shape = type === 'POINT'
      ? style.point.shape
      : undefined;
    const stylePayload = {
      color,
      weight,
      opacity,
      fillColor: type === 'POLYGON' || type === 'POINT' ? color : undefined,
      fillOpacity: type === 'POLYGON' || type === 'POINT' ? fillOpacity : undefined,
      radius,
      dashArray,
      shape,
      meta: type === 'POLYGON' ? { editorialHexified: true } : undefined
    };
    drawings.push({
      geometry: JSON.stringify(feature.geometry),
      type,
      style: stylePayload,
      properties: feature.properties || undefined
    });
  };

  // Sort points by value so higher values render on top (processed last)
  const sortField = style.point.mode === 'choropleth' ? style.point.field : style.point.sizeField;
  const sortedFeatures = sortField
    ? [...expandedFeatures].sort((a, b) => {
        const typeA = geometryToType(a.geometry);
        const typeB = geometryToType(b.geometry);
        // Only sort points, keep other geometry types in original order
        if (typeA !== 'POINT' && typeB !== 'POINT') return 0;
        if (typeA !== 'POINT') return -1; // Non-points go first
        if (typeB !== 'POINT') return 1;
        const valA = parseNumber(a.properties?.[sortField]) ?? 0;
        const valB = parseNumber(b.properties?.[sortField]) ?? 0;
        return valA - valB; // Lower values first, higher values last (on top)
      })
    : expandedFeatures;

  sortedFeatures.forEach((feature) => {
    const type = geometryToType(feature.geometry);
    if (!type) return;
    if (type === 'POLYGON') {
      const hexGeometries = buildEditorialHexCellGeometries(feature.geometry);
      hexGeometries.forEach((geometry) => {
        pushDrawing({ ...feature, geometry }, type);
      });
      return;
    }
    pushDrawing(feature, type);
  });

  if (drawings.length > MAX_FEATURES) {
    return drawings.slice(0, MAX_FEATURES);
  }
  return drawings;
};

export const limitEditorialFeatures = (features: EditorialFeature[]) => {
  if (features.length <= MAX_FEATURES) return { features, warnings: [] };
  return {
    features: features.slice(0, MAX_FEATURES),
    warnings: [`Imported ${features.length} features; only the first ${MAX_FEATURES} were kept.`]
  };
};
